import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Share2, Sparkles } from "lucide-react";
import { Orb } from "@/components/Orb";
import { WTCLogo } from "@/components/WTCLogo";
import { BusinessCard } from "@/components/BusinessCard";
import { commitToMove, createBusiness } from "@/lib/api";
import type { Business, MoveKind } from "@/lib/types";
import { cn } from "@/lib/cn";

type Step = "name" | "industry" | "scale" | "describe" | "ambition" | "generating" | "result";

const INDUSTRY_SUGGESTIONS = [
  "Hospitality",
  "Real estate",
  "Retail / e-commerce",
  "Professional services",
  "Manufacturing",
  "Health & wellness",
  "Food & beverage",
  "Construction",
  "Logistics",
  "Education",
  "Finance",
  "Media",
];

const SCALE_OPTIONS = [
  "Just me",
  "2–10 people",
  "11–50",
  "51–200",
  "200+",
];

export default function Reimagine() {
  const [step, setStep] = useState<Step>("name");
  const [creatorName, setCreatorName] = useState(() => localStorage.getItem("wtc_ai_name") ?? "");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [scale, setScale] = useState("");
  const [description, setDescription] = useState("");
  const [ambition, setAmbition] = useState("");
  const [business, setBusiness] = useState<Business | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (creatorName) localStorage.setItem("wtc_ai_name", creatorName);
  }, [creatorName]);

  const canNextName = true; // optional
  const canNextIndustry = industry.trim().length >= 2;
  const canNextScale = scale.trim().length >= 1;
  const canNextDescribe = description.trim().length >= 20;
  const canGenerate = ambition.trim().length >= 5;

  async function generate() {
    setStep("generating");
    setError(null);
    const jitter = Math.random() * 400;
    const minDisplay = 2200;
    const started = Date.now();
    try {
      await new Promise((r) => setTimeout(r, jitter));
      const b = await createBusiness({
        description,
        industry,
        scale,
        ambition,
        businessName,
        creatorName,
      });
      const elapsed = Date.now() - started;
      if (elapsed < minDisplay) {
        await new Promise((r) => setTimeout(r, minDisplay - elapsed));
      }
      setBusiness(b);
      setStep("result");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setStep("ambition");
    }
  }

  return (
    <div className="min-h-dvh">
      <div className="mx-auto flex min-h-dvh max-w-xl flex-col px-5 pb-10 pt-6">
        <Header step={step} />

        <div className="relative mt-6 flex flex-1 flex-col">
          <AnimatePresence mode="wait">
            {step === "name" && (
              <StepCard
                key="name"
                title="Tell me about you"
                hint="Optional — appears on the live wall as 'by you'."
              >
                <input
                  className="input-glass"
                  placeholder="Your first name (optional)"
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value.slice(0, 30))}
                  autoFocus
                />
                <input
                  className="input-glass"
                  placeholder="Business name (optional, e.g. Quinta Vista)"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value.slice(0, 60))}
                />
                <NextBar onNext={() => setStep("industry")} disabled={!canNextName} />
              </StepCard>
            )}

            {step === "industry" && (
              <StepCard
                key="industry"
                title="What sector?"
                hint="Pick a chip or type your own."
                onBack={() => setStep("name")}
              >
                <input
                  className="input-glass"
                  placeholder="e.g. Boutique hospitality, Olive oil producer"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  autoFocus
                />
                <Chips
                  options={INDUSTRY_SUGGESTIONS}
                  selected={industry}
                  onSelect={setIndustry}
                />
                <NextBar onNext={() => setStep("scale")} disabled={!canNextIndustry} />
              </StepCard>
            )}

            {step === "scale" && (
              <StepCard
                key="scale"
                title="How big are you?"
                hint="Rough team size."
                onBack={() => setStep("industry")}
              >
                <Chips
                  options={SCALE_OPTIONS}
                  selected={scale}
                  onSelect={setScale}
                  full
                />
                <NextBar onNext={() => setStep("describe")} disabled={!canNextScale} />
              </StepCard>
            )}

            {step === "describe" && (
              <StepCard
                key="describe"
                title="Your business in 3 sentences."
                hint="What you do, who you serve, what makes you different."
                onBack={() => setStep("scale")}
              >
                <textarea
                  className="input-glass min-h-[160px] resize-none"
                  placeholder={"e.g. We run a 12-room boutique hotel in the Alentejo. Mostly couples and small groups, mostly weekenders from Lisbon and Spain. We focus on slow stays — pairing wine with local produce, no in-room TVs."}
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 700))}
                  rows={5}
                  autoFocus
                />
                <div className="text-right text-xs text-white/40">
                  {description.length}/700 — at least 20 chars
                </div>
                <NextBar onNext={() => setStep("ambition")} disabled={!canNextDescribe} />
              </StepCard>
            )}

            {step === "ambition" && (
              <StepCard
                key="ambition"
                title="What would you love to crack?"
                hint="The thing you've been stuck on, or the bold move you've been afraid to make."
                onBack={() => setStep("describe")}
              >
                <textarea
                  className="input-glass min-h-[110px] resize-none"
                  placeholder="e.g. Doubling our shoulder-season bookings without dropping prices."
                  value={ambition}
                  onChange={(e) => setAmbition(e.target.value.slice(0, 300))}
                  rows={3}
                  autoFocus
                />
                {error && (
                  <div className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">
                    {error}
                  </div>
                )}
                <NextBar onNext={generate} disabled={!canGenerate} label="Reimagine my business" />
              </StepCard>
            )}

            {step === "generating" && <Generating key="generating" />}

            {step === "result" && business && (
              <Result
                key="result"
                business={business}
                onUpdate={setBusiness}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Header({ step }: { step: Step }) {
  const progress =
    step === "name" ? 0.05 :
    step === "industry" ? 0.18 :
    step === "scale" ? 0.32 :
    step === "describe" ? 0.55 :
    step === "ambition" ? 0.8 :
    step === "generating" ? 0.95 : 1;

  return (
    <div className="flex items-center gap-3">
      <WTCLogo className="h-6" />
      <div className="h-4 w-px bg-white/10" />
      <div className="text-xs uppercase tracking-widest text-white/60">Reimagine</div>
      <div className="ml-auto h-1.5 w-full max-w-[80px] overflow-hidden rounded-full bg-white/5">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}

function StepCard({
  title,
  hint,
  children,
  onBack,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
  onBack?: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -16 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-1 flex-col"
    >
      {onBack && (
        <button
          className="mb-2 inline-flex items-center gap-1.5 self-start text-xs text-white/50 hover:text-white"
          onClick={onBack}
        >
          <ArrowLeft className="h-3 w-3" /> back
        </button>
      )}
      <h2 className="text-4xl font-semibold leading-[1.05] text-white">{title}</h2>
      <p className="mt-2 text-sm text-white/60">{hint}</p>
      <div className="mt-6 flex flex-col gap-3">{children}</div>
    </motion.div>
  );
}

function Chips({
  options,
  selected,
  onSelect,
  full,
}: {
  options: string[];
  selected: string;
  onSelect: (v: string) => void;
  full?: boolean;
}) {
  return (
    <div className={cn("flex flex-wrap gap-2", full && "grid grid-cols-2 sm:grid-cols-3")}>
      {options.map((opt) => {
        const active = selected.trim().toLowerCase() === opt.toLowerCase();
        return (
          <button
            key={opt}
            onClick={() => onSelect(opt)}
            className={cn(
              full ? "w-full" : "",
              "rounded-md px-3 py-1.5 text-xs transition",
              active
                ? "bg-white text-ink-900"
                : "border border-white/10 bg-white/5 text-white/70 hover:bg-white/10",
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function NextBar({
  onNext,
  disabled,
  label = "Continue",
}: {
  onNext: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button className="btn-primary mt-2 w-full" onClick={onNext} disabled={disabled}>
      {label} <ArrowRight className="h-4 w-4" />
    </button>
  );
}

const HINTS = [
  "Reading what makes your business different",
  "Mapping where AI would amplify you",
  "Drafting your three bold moves",
  "Sharpening titles, first steps, 90-day outcomes",
];

function Generating() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % HINTS.length), 1700);
    return () => clearInterval(t);
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-1 flex-col items-center justify-center text-center"
    >
      <Orb size={220} intensity="thinking" />
      <motion.div
        className="mt-8 max-w-xs"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <p className="text-3xl font-semibold leading-tight text-white">
          Reimagining your business…
        </p>
        <AnimatePresence mode="wait">
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="mt-3 text-sm text-white/60"
          >
            {HINTS[i]}
          </motion.p>
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

function Result({
  business,
  onUpdate,
}: {
  business: Business;
  onUpdate: (b: Business) => void;
}) {
  const [committing, setCommitting] = useState(false);
  const [copied, setCopied] = useState(false);

  async function commit(kind: MoveKind) {
    if (committing) return;
    setCommitting(true);
    try {
      const updated = await commitToMove(business.id, kind);
      onUpdate(updated);
    } catch {
      /* ignore */
    } finally {
      setCommitting(false);
    }
  }

  async function share() {
    const url = `${window.location.origin}/b/${business.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: business.headline,
          text: business.summary,
          url,
        });
        return;
      } catch {
        /* dismissed */
      }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-1 flex-col"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 rounded-md bg-cyan-glow/10 px-3 py-1 text-[10px] uppercase tracking-widest text-cyan-glow">
          <Sparkles className="h-3 w-3" /> Three bold moves
        </div>
        <h2 className="mt-3 text-3xl font-semibold leading-tight text-white">
          Three sentences in.
          <br />
          <span className="text-cyan-glow">Three bold moves out.</span>
        </h2>
        <p className="mt-3 text-sm text-white/60">Pick the one you'll act on this month.</p>
      </div>

      <BusinessCard
        business={business}
        className="mt-6"
        highlightCommitment
        onCommit={commit}
      />

      <div className="mt-5 grid grid-cols-1 gap-3">
        <button className="btn-ghost" onClick={share}>
          {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          {copied ? "Link copied" : "Share my reimagined business"}
        </button>
      </div>

      <div className="glass mt-5 rounded-lg p-4 text-sm text-white/80">
        <div className="mb-1 text-[10px] uppercase tracking-widest text-cyan-glow">
          Your one-month commitment
        </div>
        {business.commitment
          ? `You're committing to the ${business.commitment.toUpperCase()} move. Tell one person on your team about it this week.`
          : "Tap the button on the move you'll start this month — the one most likely to move the needle."}
      </div>
    </motion.div>
  );
}
